'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useAction } from 'convex/react'
import { useUser } from '@clerk/nextjs'
import { useTranslations } from 'next-intl'
import { makeFunctionReference } from 'convex/server'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { ArrowLeft, X, Loader2, CheckCircle2, Check, AlertCircle, Sparkles } from 'lucide-react'
import { Skeleton } from '@/design-system'
import { AnalyticsEvent, trackEvent, trackPaymentSuccess } from '@/lib/analytics'
import { useCheckoutCompletion } from '@/hooks/useCheckoutCompletion'

// The LS action is not yet in the generated API (codegen runs at deploy time).
const lsInitiateCheckout = makeFunctionReference<
  'action',
  { planId?: Id<'plans'>; billingCycle?: 'monthly' | 'annually' },
  { checkoutUrl: string; trialEndsAt?: number }
>('features/billing/lsActions:initiateCheckout')

interface OnboardingCheckoutModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
  /** When true, skips the auto-complete behaviour that fires when the org already
   *  has a payment method on file. Use this for upgrade flows where the user may
   *  already be on a paid plan but wants to switch to a higher tier. */
  skipAutoComplete?: boolean
  /** Set to 'onboarding' to enable GA event tracking for the onboarding-flow
   *  funnel (Choose Plan / Checkout / Contact / Payment Success). Other usages
   *  (upgrade banner, sidebar, etc.) leave this unset and fire no events. */
  analyticsContext?: 'onboarding'
}

type Step = 'plan-selection' | 'checkout' | 'contact' | 'success'

type PublicPlan = {
  _id: Id<'plans'>
  name: string
  description?: string
  priceMonthlyUsd: number
  priceAnnuallyUsd: number
  marketingFeatures?: string[]
  isContactSales?: boolean
  freeTrialDays?: number
  displayOrder: number
  lsVariantId?: string
  lsVariantIdAnnual?: string
}

function formatPrice(cents: number): string {
  const dollars = cents / 100
  if (dollars === 0) return 'Free'
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
}

export default function OnboardingCheckoutModal({ isOpen, onClose, onComplete, skipAutoComplete = false, analyticsContext }: OnboardingCheckoutModalProps) {
  const t = useTranslations('onboarding.checkoutModal')
  const isOnboardingFlow = analyticsContext === 'onboarding'
  const [step, setStep] = useState<Step>('plan-selection')
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annually'>('monthly')
  const [selectedPlanId, setSelectedPlanId] = useState<Id<'plans'> | null>(null)
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null)
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  const publicPlans = useQuery(api.features.plans.queries.listPublicPlans)
  const referralPlan = useQuery(api.features.plans.queries.getOrgReferralPlan)
  const organizations = useQuery(api.organizations.getOrganizations)
  // Webhook-created subscription; carries lsOrderId for the GA purchase event.
  const subscription = useQuery(api.features.billing.queries.getSubscription)
  const currentPlanId = organizations?.[0]?.planId ?? null
  // Widen the plan-selection step when 3+ self-serve plans will render, so
  // three cards fit on one row next to the 300px "Looking for more?" column.
  // Counted across both billing cycles so the modal width doesn't jump when
  // the user flips the monthly/annual toggle.
  const selfServePlanCount =
    (publicPlans as PublicPlan[] | undefined)?.filter((p) => p.lsVariantId || p.lsVariantIdAnnual).length ?? 0
  const widePlanGrid = !referralPlan && selfServePlanCount >= 3
  const initiateCheckout = useAction(lsInitiateCheckout)
  const sendCustomPlanRequest = useAction(api.contactSales.sendCustomPlanRequest)
  const { user } = useUser()

  // "Looking for more?" CTA state
  const [isContactSending, setIsContactSending] = useState(false)
  const [contactSent, setContactSent] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)
  const [contactEmail, setContactEmail] = useState('')

  // Aborted flag — prevents in-flight action from updating state after modal resets
  const cancelledRef = useRef(false)

  // Checkout-completion logic extracted into shared hook (also used by PlanCards).
  const checkoutCompletion = useCheckoutCompletion({
    active: isOpen && step === 'checkout',
    onComplete,
    skipAutoComplete,
  })

  // Sync success step from hook → modal step state.
  useEffect(() => {
    if (checkoutCompletion.isSuccess && step === 'checkout') {
      setStep('success')
    }
  }, [checkoutCompletion.isSuccess, step])

  const successTimedOut = checkoutCompletion.successTimedOut

  // Fire "Choose Plan" once when the modal opens during the onboarding flow.
  useEffect(() => {
    if (isOpen && isOnboardingFlow) {
      trackEvent(AnalyticsEvent.ChoosePlan)
    }
  }, [isOpen, isOnboardingFlow])

  // Fire the post-payment events once when entering the success step. The
  // success step can be reached via the iframe postMessage before the LS
  // webhook lands, so wait for the webhook-created subscription (source of
  // the order id). If webhook confirmation timed out, fire without it rather
  // than dropping the event.
  const paymentTrackedRef = useRef(false)
  useEffect(() => {
    if (!isOpen || !isOnboardingFlow || step !== 'success') return
    if (paymentTrackedRef.current) return
    if (!subscription && !successTimedOut) return
    paymentTrackedRef.current = true
    const paidPlan = referralPlan
      ? (referralPlan as PublicPlan)
      : ((publicPlans as PublicPlan[] | undefined)?.find((p) => p._id === selectedPlanId) ?? null)
    const priceCents = paidPlan
      ? billingCycle === 'annually'
        ? paidPlan.priceAnnuallyUsd
        : paidPlan.priceMonthlyUsd
      : null
    trackPaymentSuccess({
      transactionId: subscription?.lsOrderId,
      valueUsd: priceCents != null ? priceCents / 100 : undefined,
      planId: paidPlan?._id,
      planName: paidPlan?.name,
      billingCycle,
    })
  }, [isOpen, isOnboardingFlow, step, subscription, successTimedOut, referralPlan, publicPlans, selectedPlanId, billingCycle])

  // Reset all state when modal closes
  useEffect(() => {
    if (!isOpen) {
      cancelledRef.current = true
      paymentTrackedRef.current = false
      checkoutCompletion.reset()
      setStep('plan-selection')
      setBillingCycle('monthly')
      setSelectedPlanId(null)
      setCheckoutUrl(null)
      setIsLoadingCheckout(false)
      setCheckoutError(null)
      setIsContactSending(false)
      setContactSent(false)
      setContactError(null)
      setContactEmail('')
      return
    }
    cancelledRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Auto-select the referral plan when it loads (and user hasn't manually selected another)
  useEffect(() => {
    if (!isOpen) return
    if (referralPlan && !selectedPlanId) {
      setSelectedPlanId(referralPlan._id)
    }
  }, [isOpen, referralPlan, selectedPlanId])

  // Escape key: on checkout/contact step → go back to plan-selection; on plan-selection → call onClose
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (step === 'checkout') {
          checkoutCompletion.reset()
          setStep('plan-selection')
          setCheckoutUrl(null)
          setCheckoutError(null)
        } else if (step === 'contact') {
          setStep('plan-selection')
          setContactError(null)
        } else if (step === 'plan-selection') {
          onClose()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, onClose])

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [isOpen])

  const handleOpenContactStep = () => {
    if (isOnboardingFlow) {
      trackEvent(AnalyticsEvent.OnboardingContact)
    }
    const email = user?.emailAddresses?.[0]?.emailAddress ?? ''
    setContactEmail(email)
    setContactError(null)
    setContactSent(false)
    setStep('contact')
  }

  const handleSendContactRequest = async () => {
    const trimmed = contactEmail.trim()
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
    if (!emailValid) {
      setContactError(t('contactEmailError'))
      return
    }
    setIsContactSending(true)
    setContactError(null)
    try {
      await sendCustomPlanRequest({
        userEmail: trimmed,
        organizationName: organizations?.[0]?.name ?? user?.fullName ?? trimmed,
      })
      setContactSent(true)
    } catch (err) {
      setContactError(err instanceof Error ? err.message : t('sendError'))
    } finally {
      setIsContactSending(false)
    }
  }

  const handleContinueToCheckout = async () => {
    if (!selectedPlanId) return
    if (isOnboardingFlow) {
      trackEvent(AnalyticsEvent.Checkout)
    }
    setIsLoadingCheckout(true)
    setCheckoutError(null)
    // Mark that we've entered checkout — detection in useCheckoutCompletion uses this
    // to distinguish "user just paid" from "hasPaymentMethod was already true on open".
    checkoutCompletion.markInCheckout()
    setCheckoutUrl(null)
    setStep('checkout')

    try {
      const result = await initiateCheckout({ planId: selectedPlanId, billingCycle })
      if (cancelledRef.current) return
      if (!result?.checkoutUrl) throw new Error('No checkout URL returned')
      setCheckoutUrl(result.checkoutUrl)
    } catch (err) {
      if (cancelledRef.current) return
      setCheckoutError(err instanceof Error ? err.message : t('checkoutError'))
      setStep('plan-selection')
    } finally {
      if (!cancelledRef.current) {
        setIsLoadingCheckout(false)
      }
    }
  }

  const handleBackToPlans = () => {
    checkoutCompletion.reset()
    setStep('plan-selection')
    setCheckoutUrl(null)
    setCheckoutError(null)
  }

  if (!isOpen) return null

  const headerTitle =
    step === 'plan-selection' ? t('stepTitles.planSelection') :
    step === 'checkout' ? t('stepTitles.checkout') :
    step === 'contact' ? t('stepTitles.contact') :
    t('stepTitles.success')

  const lookingForMoreCard = (
    <div
      className="relative rounded-2xl p-6 flex flex-col w-full overflow-hidden"
      style={{
        background:
          'linear-gradient(160deg, var(--color-lime-muted) 0%, var(--color-surface) 70%)',
        border: '1px solid var(--color-lime)',
      }}
    >
      {/* Decorative blurred lime accent */}
      <div
        aria-hidden
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-50 blur-2xl pointer-events-none"
        style={{ background: 'var(--color-lime)' }}
      />

      <div
        className="relative w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
        style={{ background: 'var(--color-lime)', boxShadow: '0 8px 24px -8px rgba(132, 204, 22, 0.5)' }}
      >
        <Sparkles className="w-6 h-6" style={{ color: 'var(--color-background)' }} />
      </div>

      <h4 className="relative text-xl font-bold leading-tight" style={{ color: 'var(--color-text-primary)' }}>
        {t('lookingForMore.title')}
      </h4>
      <p className="relative text-sm mt-3 leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
        Running an <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t('lookingForMore.agencyLabel')}</span> or
        an <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t('lookingForMore.enterpriseLabel')}</span>?{' '}
        {t('lookingForMore.tailored')}
      </p>

      <ul className="relative mt-4 space-y-2">
        {([
          t('lookingForMore.customLimits'),
          t('lookingForMore.successManager'),
          t('lookingForMore.sso'),
          t('lookingForMore.priorityIntegrations'),
        ] as string[]).map((feature) => (
          <li key={feature} className="flex items-center gap-2">
            <span
              className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center"
              style={{ background: 'var(--color-lime)' }}
            >
              <Check className="w-2.5 h-2.5" style={{ color: 'var(--color-background)' }} strokeWidth={3} />
            </span>
            <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{feature}</span>
          </li>
        ))}
      </ul>

      {contactSent ? (
        <p className="relative text-sm mt-4 font-medium" style={{ color: 'var(--color-lime)' }}>
          {t('lookingForMore.thanksMessage')}
        </p>
      ) : null}

      <div className="relative mt-auto pt-5">
        <button
          type="button"
          onClick={handleOpenContactStep}
          disabled={contactSent}
          className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 flex items-center justify-center gap-2"
          style={
            contactSent
              ? { background: 'var(--color-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', cursor: 'default' }
              : { background: 'var(--color-lime)', color: 'var(--color-background)' }
          }
        >
          {contactSent ? t('lookingForMore.requestSent') : t('lookingForMore.contactUs')}
        </button>
      </div>
    </div>
  )

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={step === 'plan-selection' ? onClose : undefined}
      />

      {/* Modal */}
      <div
        className="relative w-full mx-4 rounded-xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col"
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          maxWidth: step === 'plan-selection' ? (widePlanGrid ? '1180px' : '1000px') : '620px',
          height: step === 'plan-selection' || step === 'contact' ? 'auto' : 'min(720px, 90vh)',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--color-border)' }}
        >
          <div className="flex items-center gap-2">
            {(step === 'checkout' || step === 'contact') && (
              <button
                onClick={() => {
                  if (step === 'contact') {
                    setStep('plan-selection')
                    setContactError(null)
                  } else {
                    handleBackToPlans()
                  }
                }}
                className="p-1 rounded-md transition-colors hover:bg-[var(--color-surface-hover)]"
                aria-label={t('backLabel')}
              >
                <ArrowLeft className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
              </button>
            )}
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{headerTitle}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md transition-colors hover:bg-[var(--color-surface-hover)]"
          >
            <X className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        {/* Content */}
        <div
          className="w-full flex-1 min-h-0"
          style={{ overflowY: step === 'checkout' ? 'hidden' : 'auto' }}
        >
          {/* Step: Plan Selection */}
          {step === 'plan-selection' && (() => {
            const referralResolved = referralPlan === undefined ? undefined : (referralPlan as PublicPlan | null)
            const inReferralMode = !!referralResolved
            const hasMonthly = inReferralMode
              ? !!referralResolved!.lsVariantId
              : (publicPlans?.some((p) => p.lsVariantId) ?? false)
            const hasAnnual = inReferralMode
              ? !!referralResolved!.lsVariantIdAnnual
              : (publicPlans?.some((p) => p.lsVariantIdAnnual) ?? false)
            const showToggle = hasMonthly && hasAnnual
            const handleCycleChange = (cycle: 'monthly' | 'annually') => {
              setBillingCycle(cycle)
              if (!inReferralMode) setSelectedPlanId(null)
            }
            const canContinue = inReferralMode
              ? !isLoadingCheckout
              : !!selectedPlanId && selectedPlanId !== currentPlanId && !isLoadingCheckout

            return (
            <div className="p-6">
              {checkoutError && (
                <div
                  className="mb-4 rounded-lg px-4 py-3 text-sm"
                  style={{
                    color: 'var(--color-pink)',
                    background: 'var(--color-pink-muted)',
                    border: '1px solid var(--color-pink)',
                  }}
                >
                  {checkoutError}
                </div>
              )}

              {/* Billing cycle toggle — full-width, centered above the grid */}
              {showToggle && (
                <div className="flex justify-center mb-5">
                  <div
                    className="inline-flex rounded-lg border p-1"
                    style={{
                      borderColor: 'var(--color-border)',
                      background: 'var(--color-surface-hover)',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleCycleChange('monthly')}
                      className="px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-150"
                      style={
                        billingCycle === 'monthly'
                          ? {
                              background: 'var(--color-surface)',
                              color: 'var(--color-text-primary)',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                            }
                          : { color: 'var(--color-text-muted)' }
                      }
                    >
                      {t('billing.monthly')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCycleChange('annually')}
                      className="px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-150 inline-flex items-baseline gap-1.5"
                      style={
                        billingCycle === 'annually'
                          ? {
                              background: 'var(--color-surface)',
                              color: 'var(--color-text-primary)',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                            }
                          : { color: 'var(--color-text-muted)' }
                      }
                    >
                      {t('billing.annual')}
                      <span className="text-[10px] font-semibold" style={{ color: 'var(--color-lime)' }}>{t('billing.annualDiscount')}</span>
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 items-stretch">
                <div className="min-w-0 flex flex-col">

              {/* Loading skeleton — show while either query is pending */}
              {(publicPlans === undefined || referralPlan === undefined) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="rounded-xl border p-5 space-y-3" style={{ borderColor: 'var(--color-border)' }}>
                      <Skeleton className="h-5 w-24" />
                      <Skeleton className="h-8 w-20" />
                      <div className="space-y-2 pt-1">
                        <Skeleton className="h-3.5 w-full" />
                        <Skeleton className="h-3.5 w-5/6" />
                        <Skeleton className="h-3.5 w-4/6" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state — only show when no referral plan and no public plans */}
              {referralPlan === null && publicPlans !== undefined && publicPlans.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{t('noPlans')}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{t('noPlansHelp')}</p>
                </div>
              )}

              {/* Plan cards */}
              {(() => {
                // If org has a referral plan, show only that plan (even if not public)
                if (referralPlan !== undefined) {
                  if (referralPlan === null) {
                    // No referral plan — show public plans
                  } else {
                    const plan = referralPlan as PublicPlan
                    const price = billingCycle === 'annually' ? plan.priceAnnuallyUsd : plan.priceMonthlyUsd
                    const isCurrentPlan = currentPlanId === plan._id
                    return (
                      <>
                        <div>
                          {/* Single locked plan card — not clickable, always selected */}
                          <div
                            className="text-left rounded-xl border-2 shadow-sm p-5 flex flex-col"
                            style={{
                              borderColor: 'var(--color-lime)',
                              background: 'var(--color-lime-muted)',
                            }}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>{plan.name}</span>
                                {isCurrentPlan && (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                                    style={{ background: 'var(--color-lime-muted)', color: 'var(--color-lime)', border: '1px solid var(--color-lime)' }}
                                  >
                                    {t('currentPlan')}
                                  </span>
                                )}
                              </div>
                              <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'var(--color-lime)' }}>
                                <Check className="w-3 h-3" style={{ color: 'var(--color-background)' }} />
                              </span>
                            </div>

                            {plan.description && (
                              <p className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>{plan.description}</p>
                            )}

                            <div className="mb-3">
                              <span className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{formatPrice(price)}</span>
                              <span className="text-xs ml-1" style={{ color: 'var(--color-text-muted)' }}>{t(billingCycle === 'annually' ? 'perYear' : 'perMonth')}</span>
                            </div>

                            {plan.freeTrialDays != null && plan.freeTrialDays > 0 && (
                              <div>
                                <div
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mb-3"
                                  style={{ background: 'var(--color-lime-muted)', color: 'var(--color-lime)', border: '1px solid var(--color-lime)' }}
                                >
                                  {t('freeTrial', { days: plan.freeTrialDays })}
                                </div>
                              </div>
                            )}

                            {plan.marketingFeatures && plan.marketingFeatures.length > 0 && (
                              <ul className="space-y-1.5">
                                {plan.marketingFeatures.map((feature, i) => (
                                  <li key={i} className="flex items-center gap-2">
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--color-lime)' }}>
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </>
                    )
                  }
                }

                // Default: show public plans with picker
                if (publicPlans === undefined) return null
                if (publicPlans.length === 0) return null
                const allPlans = publicPlans as PublicPlan[]
                // Filter plans for the selected cycle
                const visiblePlans = allPlans.filter((p) =>
                  billingCycle === 'annually' ? p.lsVariantIdAnnual : p.lsVariantId
                )
                const currentPlan = currentPlanId
                  ? visiblePlans.find((p) => p._id === currentPlanId) ?? null
                  : null
                return (
                  <>
                    <div
                      className={`grid grid-cols-1 sm:grid-cols-2 ${
                        visiblePlans.length >= 3 ? 'lg:grid-cols-3' : ''
                      } gap-4 flex-1 auto-rows-fr`}
                    >
                      {visiblePlans.map((plan) => {
                        const isSelected = selectedPlanId === plan._id
                        const isCurrentPlan = currentPlanId === plan._id
                        // Lower-tier plans are downgrades — visually
                        // de-emphasised but still selectable. Defined as fewer
                        // marketing features than the current plan, falling
                        // back to a lower monthly price when feature lists
                        // are equal or empty.
                        const planFeatureCount = plan.marketingFeatures?.length ?? 0
                        const currentFeatureCount =
                          currentPlan?.marketingFeatures?.length ?? 0
                        const isDowngrade =
                          !!currentPlan &&
                          !isCurrentPlan &&
                          (planFeatureCount < currentFeatureCount ||
                            (planFeatureCount === currentFeatureCount &&
                              plan.priceMonthlyUsd < currentPlan.priceMonthlyUsd))
                        const price = billingCycle === 'annually' ? plan.priceAnnuallyUsd : plan.priceMonthlyUsd
                        const cardStyle: React.CSSProperties = isCurrentPlan
                          ? {
                              borderColor: 'var(--color-border)',
                              background: 'var(--color-surface)',
                              cursor: 'not-allowed',
                            }
                          : isSelected
                          ? { borderColor: 'var(--color-lime)', background: 'var(--color-lime-muted)' }
                          : {
                              borderColor: 'var(--color-border)',
                              background: 'var(--color-surface)',
                              opacity: isDowngrade ? 0.7 : 1,
                            }
                        return (
                          <button
                            key={plan._id}
                            type="button"
                            onClick={() => {
                              if (isCurrentPlan) return
                              setSelectedPlanId(plan._id)
                            }}
                            disabled={isCurrentPlan}
                            aria-disabled={isCurrentPlan}
                            className="text-left rounded-xl border-2 p-5 transition-all duration-150 focus:outline-none flex flex-col hover:shadow-sm disabled:hover:shadow-none"
                            style={cardStyle}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>{plan.name}</span>
                                {isCurrentPlan && (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                                    style={{ background: 'var(--color-lime-muted)', color: 'var(--color-lime)', border: '1px solid var(--color-lime)' }}
                                  >
                                    {t('currentPlan')}
                                  </span>
                                )}
                                {isDowngrade && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                                    style={{ background: 'var(--color-pink-muted)', color: 'var(--color-pink)', border: '1px solid var(--color-pink)' }}
                                    title={t('downgradeTitle')}
                                  >
                                    {t('downgrade')}
                                  </span>
                                )}
                              </div>
                              {isSelected && (
                                <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'var(--color-lime)' }}>
                                  <Check className="w-3 h-3" style={{ color: 'var(--color-background)' }} />
                                </span>
                              )}
                            </div>

                            {plan.description && (
                              <p className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>{plan.description}</p>
                            )}

                            <div className="mb-3">
                              <span className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{formatPrice(price)}</span>
                              <span className="text-xs ml-1" style={{ color: 'var(--color-text-muted)' }}>{t(billingCycle === 'annually' ? 'perYear' : 'perMonth')}</span>
                            </div>

                            {plan.freeTrialDays != null && plan.freeTrialDays > 0 && (
                              <div>
                                <div
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mb-3"
                                  style={{ background: 'var(--color-lime-muted)', color: 'var(--color-lime)', border: '1px solid var(--color-lime)' }}
                                >
                                  {t('freeTrial', { days: plan.freeTrialDays })}
                                </div>
                              </div>
                            )}

                            {plan.marketingFeatures && plan.marketingFeatures.length > 0 && (
                              <ul className="space-y-1.5">
                                {plan.marketingFeatures.map((feature, i) => (
                                  <li key={i} className="flex items-center gap-2">
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--color-lime)' }}>
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </>
                )
              })()}
                </div>
                <div className="hidden lg:flex">{lookingForMoreCard}</div>
              </div>

              {/* Continue to Checkout — full-width row below the grid */}
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={handleContinueToCheckout}
                  disabled={!canContinue}
                  className="px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 flex items-center gap-2"
                  style={
                    canContinue
                      ? { background: 'var(--color-lime)', color: 'var(--color-background)' }
                      : { background: 'var(--color-surface-hover)', color: 'var(--color-text-muted)', cursor: 'not-allowed' }
                  }
                >
                  {isLoadingCheckout && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('continueToCheckout')}
                </button>
              </div>

              <div className="lg:hidden mt-6">{lookingForMoreCard}</div>
            </div>
            )
          })()}

          {/* Step: Contact form */}
          {step === 'contact' && (
            <div className="p-6 flex flex-col h-full">
              <div className="max-w-md mx-auto w-full flex-1 flex flex-col">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 mx-auto"
                  style={{ background: 'var(--color-lime)', boxShadow: '0 8px 24px -8px rgba(132, 204, 22, 0.5)' }}
                >
                  <Sparkles className="w-6 h-6" style={{ color: 'var(--color-background)' }} />
                </div>

                <h3 className="text-xl font-bold text-center" style={{ color: 'var(--color-text-primary)' }}>
                  {t('contactTitle')}
                </h3>
                <p className="text-sm mt-2 text-center" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('contactSubtitle')}
                </p>

                {contactSent ? (
                  <div className="mt-6 flex flex-col items-center gap-3 flex-1 justify-center">
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center"
                      style={{ background: 'var(--color-lime-muted)' }}
                    >
                      <CheckCircle2 className="w-7 h-7" style={{ color: 'var(--color-lime)' }} />
                    </div>
                    <p className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t('contactRequestSent')}</p>
                    <p className="text-sm text-center" style={{ color: 'var(--color-text-muted)' }}>
                      {t('contactSentMessage', { email: contactEmail })}{' '}
                    </p>
                  </div>
                ) : (
                  <div className="mt-6">
                    <label
                      htmlFor="contact-email"
                      className="block text-xs font-medium uppercase tracking-wider mb-2"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      {t('contactEmailLabel')}
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => {
                        setContactEmail(e.target.value)
                        if (contactError) setContactError(null)
                      }}
                      disabled={isContactSending}
                      placeholder={t('contactEmailPlaceholder')}
                      className="w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 transition-all"
                      style={{
                        background: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-text-primary)',
                      }}
                    />
                    {contactError && (
                      <p className="text-xs mt-2" style={{ color: 'var(--color-pink)' }}>{contactError}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6 max-w-md mx-auto w-full">
                {contactSent ? (
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150"
                    style={{ background: 'var(--color-lime)', color: 'var(--color-background)' }}
                  >
                    {t('contactDone')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendContactRequest}
                    disabled={isContactSending || !contactEmail.trim()}
                    className="px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 flex items-center gap-2"
                    style={
                      isContactSending || !contactEmail.trim()
                        ? { background: 'var(--color-surface-hover)', color: 'var(--color-text-muted)', cursor: 'not-allowed' }
                        : { background: 'var(--color-lime)', color: 'var(--color-background)' }
                    }
                  >
                    {isContactSending && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t('contactSendRequest')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step: Checkout iframe */}
          {step === 'checkout' && (
            <>
              {isLoadingCheckout || !checkoutUrl ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{t('loadingPayment')}</p>
                </div>
              ) : (
                <iframe
                  src={checkoutUrl}
                  className="w-full h-full border-0"
                  title="Checkout"
                  allow="payment"
                  sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
                />
              )}
            </>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="flex flex-col items-center justify-center h-full gap-3 p-6">
              {successTimedOut ? (
                <>
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(245, 158, 11, 0.15)' }}
                  >
                    <AlertCircle className="w-8 h-8" style={{ color: '#f59e0b' }} />
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>{t('success.received')}</h3>
                    <p className="text-sm mt-1 max-w-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {t.rich('success.delayed', { strong: (chunks) => <strong key="link">{chunks}</strong> })}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--color-lime-muted)' }}
                  >
                    <CheckCircle2 className="w-8 h-8" style={{ color: 'var(--color-lime)' }} />
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>{t('success.complete')}</h3>
                    <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>{t('success.setup')}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
